import { useState } from 'react';
import { LitSquadInventoryEntry } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { resolveLitSquadDisplayName } from '../utils';

interface DeleteItemDialogProps {
  item: LitSquadInventoryEntry | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeleteItemDialog({ item, open, onClose, onConfirm }: DeleteItemDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Burn failed');
    } finally {
      setSubmitting(false);
    }
  };

  const title = item ? resolveLitSquadDisplayName(item) : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Burn {title}?</DialogTitle>
          <DialogDescription>
            This action is irreversible. The NFT will be permanently destroyed.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Burning...' : 'Burn'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
